import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {CountAdapter} from '@drl/core/api/adapters/count-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import { IpsTranslateService } from '@drl/core/localization/ips-translate.service';
import {Count} from '@drl/core/models';
import {
  ChatMessageAdapter,
  ChatMessageRecipientAdapter,
} from '@drl/notification/adapters';
import {ChannelKeys} from '@drl/notification/channels-helper';
import {ChatMessage, MessageRecipient} from '@drl/notification/models';
import {GetChatMessagesCommand} from '@drl/shared/commands';
import * as moment from 'moment';
import {throwError} from 'rxjs';
import {concatMap, tap} from 'rxjs/operators';

import {
  ForwardMessageByIdCommand,
  GetChatMessageByIdCommand,
  GetChatMessageRecipientsCommand,
  GetChatMessageUnreadCountCommand,
  GetChatUnreadPatientsCountCommand,
  PatchChatMessageRecipientCommand,
  SendChatMessageCommand,
  UploadFileForChatCommand,
} from './commands';
import {MessageDto} from './models';

const offset = 20;

@Injectable()
export class ChatFacadeService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly countAdapter: CountAdapter,
    private readonly chatMsgAdapter: ChatMessageAdapter,
    private readonly chatMsgRecAdapter: ChatMessageRecipientAdapter,
    private readonly translateSvc: IpsTranslateService,
  ) {}

  messagePageSize = offset;

  getMessagesUnreadCount(
    patientUserId?: number,
    channelKey?: ChannelKeys,
    categoryId?: number,
    toUserId?: number,
  ) {
    const command: GetChatMessageUnreadCountCommand<Count> =
      new GetChatMessageUnreadCountCommand(this.apiService, this.countAdapter);
    const queryData = {
      patientUserId,
      categoryId: undefined,
      toUserId: undefined,
      channelKey: channelKey,
    };

    if (categoryId) {
      queryData.categoryId = categoryId;
    }

    if (toUserId) {
      queryData.toUserId = toUserId;
    }

    const queryStr = JSON.stringify(queryData);

    command.parameters = {
      query: new HttpParams().set('where', queryStr),
    };

    return command.execute();
  }

  getChatUnreadPatientsCount(
  ) {
    const command: GetChatUnreadPatientsCountCommand<Count> =
      new GetChatUnreadPatientsCountCommand(this.apiService, this.countAdapter);
  
    return command.execute();
  }

  getMessageById(messageId: string) {
    const command: GetChatMessageByIdCommand<ChatMessage> =
      new GetChatMessageByIdCommand(
        this.apiService,
        this.chatMsgAdapter,
        messageId,
      );

    return command.execute();
  }

  getMessages(
    queryData: object,
    order: string[],
    offsetIn = 0,
    limit = this.messagePageSize,
  ) {
    const command: GetChatMessagesCommand<ChatMessage> =
      new GetChatMessagesCommand(this.apiService, this.chatMsgAdapter);

    const queryStr = JSON.stringify({
      where: queryData,
      offset: offsetIn,
      limit,
      order,
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  sendMessage(msg: MessageDto) {
    const command: SendChatMessageCommand<MessageDto> =
      new SendChatMessageCommand(this.apiService, this.anyAdapter);
    delete msg.message.categoryId;
    command.parameters = {
      data: msg,
    };
    return command.execute();
  }

  sendFile(imageFormData: FormData, data: MessageDto) {
    const uploadCmd: UploadFileForChatCommand<unknown> =
      new UploadFileForChatCommand(this.apiService, this.anyAdapter);

    uploadCmd.parameters = {
      data: imageFormData,
    };

    const command: SendChatMessageCommand<MessageDto> =
      new SendChatMessageCommand(this.apiService, this.anyAdapter);

    return uploadCmd.execute().pipe(
      concatMap(resp => {
        const fileKey = (resp as {fileKey: string}).fileKey;
        if (!fileKey) {
          throwError(
            new Error(
              'Error while uploading file. Please try after some time.',
            ),
          );
        }

        data.message.contentId = fileKey;
        data.message.contentType = 'file';
        delete data.message.categoryId;
        command.parameters = {
          data,
        };
        return command.execute();
      }),
    );
  }

  forwardMessage(msg: ChatMessage, recipientId: number) {
    if (msg && msg.id) {
      const command: ForwardMessageByIdCommand<MessageRecipient> =
        new ForwardMessageByIdCommand(this.apiService, this.anyAdapter, msg.id);

      command.parameters = {
        data: {
          isForwarded: true,
          recipientId,
        },
      };
      return command.execute();
    }
    return throwError('Invalid input');
  }

  toggleFavorite(msgRecipient: MessageRecipient) {
    if (msgRecipient && msgRecipient.id) {
      const command: PatchChatMessageRecipientCommand<MessageRecipient> =
        new PatchChatMessageRecipientCommand(this.apiService, this.anyAdapter);
      const queryStr = JSON.stringify({
        id: msgRecipient.id,
      });
      command.parameters = {
        data: {
          isFavorite: !msgRecipient.isFavorite,
        },
        query: new HttpParams().set('where', queryStr),
      };
      return command.execute();
    }
    return throwError('Invalid input');
  }

  markAsRead(recipientId: number,patientUserId?: number) {
    const command: PatchChatMessageRecipientCommand<MessageRecipient> =
      new PatchChatMessageRecipientCommand(this.apiService, this.anyAdapter);
    const queryStr = JSON.stringify({
      // messageId: {
      //   inq: messageIds,
      // },
      patientUserId:patientUserId,
      isRead:false,
      recipientId,
    });
    command.parameters = {
      data: {
        isRead: true,
      },
      query: new HttpParams().set('where', queryStr),
    };
    return command.execute();
  }

  getFavorites(patientUserId: number, recipientId: number) {
    const command: GetChatMessageRecipientsCommand<MessageRecipient> =
      new GetChatMessageRecipientsCommand(
        this.apiService,
        this.chatMsgRecAdapter,
      );

    const queryStr = JSON.stringify({
      where: {
        patientUserId,
        recipientId,
        isFavorite: true,
      },
      order: ['modifiedOn DESC'],
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  getForwards(patientUserId: number, recipientId: number) {
    const command: GetChatMessageRecipientsCommand<MessageRecipient> =
      new GetChatMessageRecipientsCommand(
        this.apiService,
        this.chatMsgRecAdapter,
      );

    const queryStr = JSON.stringify({
      where: {
        patientUserId,
        recipientId,
        isForwarded: true,
      },
      order: ['modifiedOn DESC'],
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }
  getLastChatMessageByCategory( patientUserId, sortOrder, lastChatMap) {
    const hoursInDay = 24;
    const twentyFourHourBack = moment().subtract(hoursInDay, 'h');
    const queryData = {
      patientUserId: patientUserId,
      // categoryId,
      channelKey: ChannelKeys.PatientChannel,
      // createdOn: {
      //   lt: new Date(),
      // },
    };

    return this.getMessages(queryData, [sortOrder], 0, 1).pipe(
      tap(
        res => {
          if (res.length > 0) {
            const lastMsg = res[0];
            // if (moment(lastMsg.createdOn).isBefore(twentyFourHourBack)) {
            //   lastChatMap[
            //     categoryId
            //   ] = `${this.translateSvc.translate.instant(
            //     'lastChatLbl',
            //   )} ${moment(lastMsg.createdOn).fromNow()}`;
            // } else {
            //   lastChatMap[categoryId] =
            //     this.translateSvc.translate.instant('ongoingChatLbl');
            // }
          } else {
            // lastChatMap[categoryId] =
            //   this.translateSvc.translate.instant('noMsgsLbl');
          }
        }
      ),
    );
  }
}
